import { listConversation } from "@rubi/api-generated/clientRs";
import { useInfiniteQuery } from "@tanstack/react-query";

type UseConversationsInfiniteQueryParams = {
  rubiOrgId: string | null | undefined;
  smsPhoneNumber: string | null | undefined;
  itemsPerPage: number;
  conversationClientId?: string | null;
  autoReplyEnabled?: boolean | null;
  includeParticipants?: boolean;
  includeInboundMessages?: boolean;
  includeOutboundMessages?: boolean;
};

export function useConversationsInfiniteQuery({
  rubiOrgId,
  smsPhoneNumber,
  itemsPerPage,
  conversationClientId = null,
  autoReplyEnabled = null,
  includeParticipants = true,
  includeInboundMessages = true,
  includeOutboundMessages = true,
}: UseConversationsInfiniteQueryParams) {
  return useInfiniteQuery(
    [
      "conversations",
      rubiOrgId,
      smsPhoneNumber,
      itemsPerPage,
      conversationClientId,
      autoReplyEnabled,
      includeParticipants,
      includeInboundMessages,
      includeOutboundMessages,
    ],
    async ({ pageParam = 0 }) => {
      if (!rubiOrgId || !smsPhoneNumber || !itemsPerPage) {
        return {
          conversations: [],
          limit: itemsPerPage,
          offset: pageParam,
          total_count: 0,
        };
      }

      const response = await listConversation({
        query: {
          rubi_org_id: rubiOrgId,
          sms_phone_number: smsPhoneNumber,
          conversation_client_id: conversationClientId ?? undefined,
          auto_reply_enabled: autoReplyEnabled ?? undefined,
          include_participants: includeParticipants,
          include_inbound_messages: includeInboundMessages,
          include_outbound_messages: includeOutboundMessages,
          limit: itemsPerPage,
          offset: pageParam,
        },
      });

      return response.data;
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined;
        }

        const lastOffset = lastPage.offset ?? 0;
        const pageSize = lastPage.limit ?? itemsPerPage;
        const fetchedCount = lastPage.conversations?.length ?? 0;
        const nextOffset = lastOffset + fetchedCount;

        if (!pageSize || fetchedCount < pageSize) {
          return undefined;
        }

        if (
          typeof lastPage.total_count === "number" &&
          nextOffset >= lastPage.total_count
        ) {
          return undefined;
        }

        return nextOffset;
      },
      staleTime: 5 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
    },
  );
}
